import { DateTime } from "luxon";
import TextType from "@/inside/models/Text/Type";
import Thematic from "@/inside/models/Text/Thematic";
import Amendment from "@/inside/models/Text/Amendment";

export default class Text {
    constructor() {
        this.file = null;
        this.id = null;
        this.internalID = null;
        this.isFinished = true;
        this.isInformational = false;
        this.isLocal = false;
        this.label = null;
        this.language = "fr";
        this.lastAmendment = null;
        this.link = null;
        this.nbAssessableRequirements = 0;
        this.reference = null;
        this.thematics = [];
        this.type = null;
        this.hasSynthesis = false;

        this.date = {
            abrogation: null,
            effective: null,
            publication: null,
            signature: null,
            creation: null,
            amendment: null
        };

        this.highlight = {
            new: false,
            amended: false
        };

        this.isAmended = false;
        this.isNew = false;
        this.keywords = [];
    }

    /**
     * Returns a new `Text` instance hydrated using data from given `api_resource`.
     * @param {object} api_resource
     * @return {Text}
     */
    static fromAPI(api_resource) {
        if (typeof api_resource !== "object") {
            throw new TypeError(`Text.fromAPI() method expects parameter 1 to be an object, ${typeof api_resource} given`);
        }

        let out = Object.assign(new Text(), {
            id: api_resource.id,
            internalID: api_resource.internalID,
            isInformational: api_resource.isInformational,
            label: api_resource.label,
            language: api_resource.language,
            link: api_resource.link,
            reference: api_resource.reference,
            thematics: api_resource.thematics.map((thematic_resource) => {
                return Thematic.fromAPI(thematic_resource);
            }),
            type: (api_resource.type
                ? Object.freeze(TextType.fromAPI(api_resource.type))
                : null
            )
        });

        if (Object.prototype.hasOwnProperty.call(api_resource, "isFinished")) {
            out.isFinished = api_resource.isFinished;
        }

        if(api_resource.date.abrogation !== null){
            out.date.abrogation = DateTime.fromISO(api_resource.date.abrogation);
        }

        if(api_resource.date.publication !== null){
            out.date.publication = DateTime.fromISO(api_resource.date.publication)
        }

        if(api_resource.date.effective){
            out.date.effective = DateTime.fromISO(api_resource.date.effective)
        }

        if(api_resource.date.signature){
            out.date.signature = DateTime.fromISO(api_resource.date.signature)
        }

        if(api_resource.date.creation){
            out.date.creation = DateTime.fromISO(api_resource.date.creation)
        }

        if(api_resource.date.amendment){
            out.date.amendment = DateTime.fromISO(api_resource.date.amendment)
        }

        if(api_resource.file){
            out.file = api_resource.file
        }

        if(api_resource.isLocal){
            out.isLocal = api_resource.isLocal
        }

        if (api_resource.lastAmendment) {
            out.lastAmendment = Object.freeze(Amendment.fromAPI(api_resource.lastAmendment));
        }

        if(api_resource.nbAssessableRequirements){
            out.nbAssessableRequirements = api_resource.nbAssessableRequirements
        }

        if(api_resource.hasSynthesis){
            out.hasSynthesis = api_resource.hasSynthesis
        }

        if(api_resource.highlight){
            out.highlight = Object.assign(out.highlight, {
                new: api_resource.highlight.new,
                amended: api_resource.highlight.amended
            })
        }

        if (Object.prototype.hasOwnProperty.call(api_resource, "keywords")) {
            out.keywords = api_resource.keywords;
        }

        return out;
    }

    /**
     * Is text abrogated?
     * @return {boolean}
     */
    get isAbrogated() {
        return this.date.abrogation !== null && (this.date.abrogation < DateTime.now());
    }

    /**
     * Is text assessable?
     * @return {boolean}
     */
    get isAssessable() {
        return (!this.isInformational && !this.isAbrogated);
    }

    /**
     * Returns the latest date, among this text's signature date and its last amendment signature, as an instance of
     * `DateTime`. If no date is set, this method returns `null`.
     * @return {DateTime|null}
     */
    get latestDate() {
        if (this.lastAmendment !== null && this.lastAmendment.date.signature !== null) {
            return this.lastAmendment.date.signature;
        } else if (this.date.signature !== null) {
            return this.date.signature;
        } else {
            return null;
        }
    }
}
